import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";

import Header from "../../components/ui/Header";
import Breadcrumb from "../../components/ui/Breadcrumb";
import { getVideoJobStatus } from "../../lib/api";

export default function AnalysisResultVideo() {
  const { jobId } = useParams();
  const location = useLocation();
  const initial = location.state;

  const API_BASE = import.meta.env.VITE_API_BASE_URL;

  const [jobStatus, setJobStatus] = useState("waiting"); // waiting | processing | completed | error
  const [resultJobId, setResultJobId] = useState(null);
  const [graftCount, setGraftCount] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const breadcrumbItems = [
    { label: "Dashboard", path: "/dashboard" },
    { label: "Video Result", path: "#" },
  ];

  /* ---------------------- POLLING ---------------------- */
  useEffect(() => {
    if (!jobId) return;

    const poll = async () => {
      try {
        const res = await getVideoJobStatus(jobId);
        console.log("VIDEO STATUS RESPONSE:", res);

        if (!res || !res.status) return false;

        const status = res.status.toLowerCase();

        if (status === "queued" || status === "processing") {
          setJobStatus("processing");
          return false;
        }

        if (status === "completed") {
          setJobStatus("completed");

          if (typeof res.graft_count === "number") {
            setGraftCount(res.graft_count);
          }

          if (typeof res.video_url === "string") {
            const url = res.video_url;

            if (url.includes("/videos/") && url.includes("/processed")) {
              const afterVideos = url.split("/videos/")[1];
              if (afterVideos) {
                const folder = afterVideos.split("/")[0];
                if (folder.startsWith("gc-video-")) {
                  setResultJobId(folder);
                } else {
                  setError("Processed video ID could not be resolved.");
                  setJobStatus("error");
                }
              }
            } else {
              setError("Processed video URL format is invalid.");
              setJobStatus("error");
            }
          } else {
            setError("Processed video URL is missing.");
            setJobStatus("error");
          }

          return true;
        }

      } catch (err) {
        console.error("Polling error:", err);
        setError("An error occurred while fetching video status.");
        setJobStatus("error");
        return true;
      }

      return false;
    };

    const interval = setInterval(async () => {
      const done = await poll();
      if (done) clearInterval(interval);
    }, 3000);

    poll();
    return () => clearInterval(interval);
  }, [jobId]);

  /* ------------------- DOWNLOAD ------------------- */
  const downloadVideo = async () => {
    if (!resultJobId) {
      setError("Download is not ready yet.");
      return;
    }

    try {
      setDownloading(true);
      setError(null);

      const res = await fetch(
        `${API_BASE}/video/${resultJobId}/download`
      );

      if (!res.ok) throw new Error("Download failed");

      const contentType = res.headers.get("content-type") || "";
      let downloadUrl = null;

      if (contentType.includes("application/json")) {
        const data = await res.json();
        downloadUrl = data?.url;
      } else {
        const text = await res.text();
        if (text.startsWith("http")) downloadUrl = text;
      }

      if (!downloadUrl) throw new Error("Download URL not found");

      window.location.href = downloadUrl;
    } catch (err) {
      console.error(err);
      setError("Could not download the video.");
    } finally {
      setDownloading(false);
    }
  };

  /* ---------------------- RENDER ---------------------- */
  return (
    <div className="min-h-screen w-full bg-black text-white">
      <Header />

      <main className="pt-10 px-6 pb-20">
        <div className="max-w-3xl mx-auto">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-8 bg-white/5 border border-white/10 rounded-3xl p-8 shadow-xl">
            <h2 className="text-2xl font-light mb-2">
              Video Graft Analysis Result
            </h2>

            {initial?.patientName && (
              <p className="text-slate-300 text-sm mb-6">
                Patient: {initial.patientName}
              </p>
            )}

            {error && (
              <p className="text-red-400 mb-4">{error}</p>
            )}

            {/* -------- WAITING / PROCESSING -------- */}
            {(jobStatus === "waiting" || jobStatus === "processing") && (
              <p className="text-slate-300 mb-6">
                Your video is being analyzed.
                <br />
                Please wait, this may take a few minutes.
              </p>
            )}

            {/* -------- COMPLETED -------- */}
            {jobStatus === "completed" && (
              <>
                <p className="text-slate-300 mb-6">
                  Your video analysis has been completed.
                  <br />
                  Please download the processed video below.
                </p>

                {graftCount !== null && (
                  <div className="text-lg font-semibold mb-6">
                    Total Grafts Counted:
                    <span className="text-cyan-300 ml-2">
                      {graftCount}
                    </span>
                  </div>
                )}

                {resultJobId && (
                  <button
                    onClick={downloadVideo}
                    disabled={downloading}
                    className="px-6 py-3 rounded-full bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 transition shadow-lg"
                  >
                    {downloading
                      ? "Preparing download…"
                      : "Download Video"}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
